import React, { FC, useState } from 'react';
import { DrawerPanel } from '../ui/DrawerPanel';
import { useTranslation } from 'react-i18next';
import { createBillingPortalSession, billingPortalErrorKey } from '../../lib/billingPortal';

interface BillingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  trialEndsAt?: string;
  subscriptionStatus?: 'trial' | 'active' | 'cancelled' | 'expired';
}

/**
 * Billing panel — plan summary + link to Stripe Customer Portal for card management.
 * Spec: AidFone_Johnley_Briefing_March2026.docx §2 "Payment / Billing Panel"
 */
export const BillingDrawer: FC<BillingDrawerProps> = ({
  isOpen,
  onClose,
  seniorId,
  trialEndsAt,
  subscriptionStatus,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleManageBilling = async () => {
    setLoading(true);
    setError(null);
    try {
      // Shared with MyAccount — see lib/billingPortal.ts. Both surfaces had
      // their own copy of this and both were broken differently.
      const result = await createBillingPortalSession();
      if (result.ok) {
        window.location.href = result.url;
        return;
      }
      console.error('[BillingDrawer] Portal error:', result);
      setError(t(billingPortalErrorKey(result)));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return iso;
    }
  };

  return (
    <DrawerPanel isOpen={isOpen} onClose={onClose} title="Billing">
      <div className="flex flex-col gap-4">
        {/* Plan summary */}
        <div className="p-4 rounded-xl bg-brand-bg-warm border border-brand-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-brand-primary">shield</span>
            <h3 className="font-display font-bold text-brand-text">Protection Plan</h3>
          </div>
          <p className="text-2xl font-display font-bold text-brand-text">
            $10.99 <span className="text-sm font-normal text-brand-text-muted">CAD/month</span>
          </p>
          {subscriptionStatus === 'trial' && (
            <p className="text-xs text-brand-text-muted mt-1">
              Trial ends {formatDate(trialEndsAt)} · first charge on that date
            </p>
          )}
          {subscriptionStatus === 'active' && (
            <p className="text-xs text-emerald-600 mt-1 font-semibold">Active subscription</p>
          )}
          {subscriptionStatus === 'cancelled' && (
            <p className="text-xs text-red-600 mt-1 font-semibold">Cancelled</p>
          )}
        </div>

        <p className="text-sm text-brand-text-muted">
          Manage your payment method, update your card, or cancel your subscription through the
          Stripe Customer Portal.
        </p>

        <button
          onClick={handleManageBilling}
          disabled={loading}
          className="w-full py-3 bg-brand-primary text-white font-semibold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {/* Both strings were hardcoded English here while MyAccount's
              equivalent button was translated — so the same action read
              differently depending on which surface you opened it from.
              `account.manageBilling` already carries the approved French
              ("Gérer la facturation"); `billingPortal.opening` is new. */}
          {loading ? (
            t('billingPortal.opening')
          ) : (
            <>
              {t('account.manageBilling')}
              <span className="material-symbols-outlined text-lg">open_in_new</span>
            </>
          )}
        </button>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <p className="text-xs text-brand-text-muted text-center">
          You'll be redirected to Stripe's secure billing portal — billing history,
          card changes, and cancelling your subscription all live there.
        </p>
      </div>
    </DrawerPanel>
  );
};

export default BillingDrawer;
